import React, { useEffect, useRef } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { isAdminRole } from "../utils/roles";
import Navbar from "./Navbar";
import Footer from "./Footer";
import AppIcon from "./AppIcon";
import KakaoInquiry from "./KakaoInquiry";

function BrandPanel() {
  return (
    <aside className="ik-brand-panel" aria-label="일광오피스 안내">
      <div className="ik-brand-panel__content">
        <Link to="/" className="ik-brand-sign" aria-label="일광오피스 홈">
          <span className="ik-brand-sign__symbol"><AppIcon name="printer" size={32} /></span>
          <span>일광오피스<small>ILKWANG OFFICE</small></span>
        </Link>
        <p className="ik-brand-panel__eyebrow">일에만 집중할 수 있도록</p>
        <h2>사무실에 필요한<br />모든 것,<br /><em>일광에서.</em></h2>
        <p className="ik-brand-panel__description">복합기 렌탈부터 토너 구매, 유지보수까지.<br />우리 사무실에 꼭 맞는 선택을 만나보세요.</p>
        <div className="ik-brand-panel__actions">
          <Link to="/rental"><AppIcon name="printer" size={21} /> 우리 사무실 복합기 찾기 <AppIcon name="arrow" size={20} /></Link>
          <Link to="/consultation?tab=rental"><AppIcon name="chat" size={21} /> 맞춤 견적 상담하기 <AppIcon name="arrow" size={20} /></Link>
        </div>
        <div className="ik-brand-panel__contact">
          <a href="tel:1600-7542"><AppIcon name="phone" size={19} />1600-7542</a>
          <span>평일 09:00–18:00</span>
        </div>
        <p className="ik-brand-panel__footnote">전국 설치 · 정기 토너 공급 · A/S</p>
      </div>
    </aside>
  );
}

function BottomNav() {
  const { user } = useAuth();
  const location = useLocation();
  const accountPath = user ? (isAdminRole(user.role) ? "/admin" : "/my") : "/login";
  const items = [
    { to: "/", label: "홈", icon: "home", end: true },
    { to: "/store", label: "스토어", icon: "store" },
    { to: "/rental", label: "렌탈", icon: "printer" },
    { to: "/consultation", label: "상담", icon: "chat" },
    { to: accountPath, label: user && isAdminRole(user.role) ? "관리" : "마이", icon: "user" },
  ];
  return (
    <nav className="ik-app-bottom-nav" aria-label="앱 하단 메뉴">
      {items.map(({ to, label, icon, end }) => (
        <NavLink key={icon} to={to} end={end} state={to === "/login" ? { from: `${location.pathname}${location.search}` } : undefined}>
          <AppIcon name={icon} size={23} /><span>{label}</span>
        </NavLink>
      ))}
    </nav>
  );
}

export default function AppShell({ children }) {
  const { pathname } = useLocation();
  const shellRef = useRef(null);
  const panelRef = useRef(null);
  const bare = pathname === "/admin" || pathname.startsWith("/admin/");
  // The customer ERP already has its own mobile navigation.
  const hasBottomNav = !bare && pathname.replace(/\/+$/, "") !== "/my";

  useEffect(() => {
    const panel = panelRef.current;
    const shell = shellRef.current;
    if (!panel || !shell) return undefined;
    // Fixed purchase bars, dialogs and navigation share the actual panel bounds,
    // including browser scrollbars and resizing across the desktop breakpoint.
    const syncBounds = () => {
      const { left, width } = panel.getBoundingClientRect();
      shell.style.setProperty("--ik-panel-left", `${left}px`);
      shell.style.setProperty("--ik-panel-width", `${width}px`);
    };
    syncBounds();
    const observer = new ResizeObserver(syncBounds);
    observer.observe(panel);
    window.addEventListener("resize", syncBounds);
    return () => { observer.disconnect(); window.removeEventListener("resize", syncBounds); };
  }, [bare]);

  useEffect(() => {
    const viewport = window.visualViewport;
    if (!viewport) return undefined;
    const syncKeyboard = () => {
      const editing = document.activeElement?.matches("input, textarea, select, [contenteditable='true']");
      const open = editing && window.innerHeight - viewport.height > 150 && viewport.scale === 1;
      shellRef.current?.classList.toggle("ik-keyboard-open", Boolean(open));
    };
    viewport.addEventListener("resize", syncKeyboard);
    document.addEventListener("focusin", syncKeyboard);
    document.addEventListener("focusout", syncKeyboard);
    return () => {
      viewport.removeEventListener("resize", syncKeyboard);
      document.removeEventListener("focusin", syncKeyboard);
      document.removeEventListener("focusout", syncKeyboard);
    };
  }, []);

  if (bare) return (
    <div className="ik-admin-viewport">
      <div className="department-site min-h-screen flex flex-col bg-white">
        <main className="department-main flex-1" data-route={pathname}>{children}</main>
      </div>
    </div>
  );

  return (
    <div ref={shellRef} className={`ik-app-shell${hasBottomNav ? " has-bottom-nav" : ""}`}>
      <a className="ik-skip-link" href="#ik-main">본문으로 바로가기</a>
      <div className="ik-app-layout">
        <BrandPanel />
        <div ref={panelRef} className="ik-app-panel">
          <div className="department-site min-h-screen flex flex-col bg-white">
            <Navbar />
            <main id="ik-main" className="department-main flex-1" data-route={pathname} tabIndex={-1}>{children}</main>
            <Footer />
            {hasBottomNav && <BottomNav />}
            <KakaoInquiry />
          </div>
        </div>
      </div>
    </div>
  );
}
